import { useEffect, type ReactNode } from 'react';
import { useWorkspaceStore } from '@/store/workspaceStore';

/**
 * Phase 12.1 — initializes workspace context on mount and applies brand color
 * as a CSS variable on document root so any component can pick it up via
 * `var(--workspace-brand)`. On main domain (no subdomain) it just calls
 * detect() once and clears the variable.
 *
 * Does NOT block rendering: while `loading=true` or before detect finishes,
 * children render normally. App.tsx separately checks `error` and swaps the
 * whole tree for a blocker screen if the workspace is archived/missing.
 */
export default function WorkspaceProvider({ children }: { children: ReactNode }) {
  const detect = useWorkspaceStore((s) => s.detect);
  const current = useWorkspaceStore((s) => s.current);

  useEffect(() => {
    void detect();
  }, [detect]);

  useEffect(() => {
    const root = document.documentElement;
    if (current?.brandColor) {
      root.style.setProperty('--workspace-brand', current.brandColor);
    } else {
      root.style.removeProperty('--workspace-brand');
    }
    return () => {
      root.style.removeProperty('--workspace-brand');
    };
  }, [current?.brandColor]);

  return <>{children}</>;
}
